// Settings → Invitations. Admin-only self-service onboarding: mint a single-use, time-limited
// invite link that lets the recipient set their OWN email + password. The raw token / one-time
// URL is returned EXACTLY ONCE (never retrievable again), so the one-time panel makes that
// unmistakable and offers a copy button. Mirrors AgentsCard: the whole card is hidden for
// non-admins and every mutation goes through the CSRF-protected, audit-logged client.

import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import type { InviteRole, InviteSummary } from '../types/api';
import { C, MONO } from '../lib/tokens';
import { PBTN, GBTN_SM, H2, badgeS, mTh, mTd } from '../lib/styles';
import { parseTs, fmtTS, fmtAge } from '../lib/format';
import { useAuth, useServerNow } from '../store/hooks';

type OneTime = { id: string; url: string; role: InviteRole; email: string | null; expires_at: string };

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  return fallback;
}

const ROLES: InviteRole[] = ['viewer', 'operator', 'admin'];
const EXPIRY_OPTIONS: Array<[number, string]> = [
  [24, '24 hours'],
  [72, '3 days'],
  [168, '7 days'],
  [336, '14 days'],
];

const INPUT: React.CSSProperties = {
  height: 32,
  background: '#0A0F19',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  color: C.tx,
  fontSize: 12.5,
  padding: '0 10px',
  fontFamily: 'inherit',
};

export function InvitesCard() {
  const role = useAuth((s) => s.user?.role);
  const serverNow = useServerNow();

  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newRole, setNewRole] = useState<InviteRole>('viewer');
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState<number>(72);

  const [busyId, setBusyId] = useState<string | null>(null); // row id (or '*' for create) in flight
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [oneTime, setOneTime] = useState<OneTime | null>(null);
  const [copied, setCopied] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.listInvites();
      setInvites(res.invites);
      setError(null);
    } catch (e) {
      setError(errText(e, 'Could not load invitations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role !== 'admin') return;
    void refresh();
  }, [role, refresh]);

  // Only admins see this section at all.
  if (role !== 'admin') return null;

  async function create(): Promise<void> {
    if (busyId) return;
    const trimmed = email.trim();
    setBusyId('*');
    setError(null);
    setNotice(null);
    try {
      const res = await api.createInvite({
        role: newRole,
        email: trimmed ? trimmed : undefined,
        expires_in_hours: hours,
      });
      setOneTime({ id: res.id, url: res.url, role: res.role, email: res.email, expires_at: res.expires_at });
      setCopied(false);
      setEmail('');
      await refresh();
    } catch (e) {
      setError(errText(e, 'Could not create the invitation.'));
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(id: string): Promise<void> {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      await api.deleteInvite(id);
      setNotice('Invitation revoked. Its link can no longer be used.');
      if (oneTime?.id === id) setOneTime(null);
      setConfirmId(null);
      await refresh();
    } catch (e) {
      setError(errText(e, 'Could not revoke the invitation.'));
    } finally {
      setBusyId(null);
    }
  }

  async function copyUrl(): Promise<void> {
    if (!oneTime) return;
    try {
      await navigator.clipboard.writeText(oneTime.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Could not copy to clipboard — select the link and copy manually.');
    }
  }

  return (
    <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={H2}>Invitations</h2>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.dm, background: 'rgba(255,255,255,.04)', border: '1px solid ' + C.ln, borderRadius: 4, padding: '2px 8px' }}>
          Admin
        </span>
      </div>

      <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
        Invite a teammate to create their own account. Each invitation is single-use, expires automatically, and its link is shown
        here exactly once — copy it and share it over a trusted channel. The recipient sets their own password; you never see it.
      </div>

      {notice && (
        <div role="status" style={{ fontSize: 11.5, color: C.gn, background: 'rgba(63,191,127,.1)', border: '1px solid rgba(63,191,127,.35)', borderRadius: 6, padding: '7px 10px' }}>
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" style={{ fontSize: 11.5, color: C.rd, background: 'rgba(228,91,91,.1)', border: '1px solid rgba(228,91,91,.35)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      {/* Create-invite form */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Role</span>
          <select value={newRole} onChange={(e) => setNewRole(e.target.value as InviteRole)} aria-label="Invite role" style={{ ...INPUT, minWidth: 120 }}>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 180 }}>
          <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Email (optional)</span>
          <input
            ref={emailRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="pin to an address (optional)"
            maxLength={254}
            aria-label="Invite email (optional)"
            style={{ ...INPUT, width: '100%' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Expires in</span>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label="Invite expiry" style={{ ...INPUT, minWidth: 110 }}>
            {EXPIRY_OPTIONS.map(([h, label]) => (
              <option key={h} value={h}>{label}</option>
            ))}
          </select>
        </label>
        <button
          onClick={create}
          disabled={busyId === '*'}
          style={{ ...PBTN, height: 32, padding: '0 14px', opacity: busyId === '*' ? 0.6 : 1, cursor: busyId === '*' ? 'default' : 'pointer' }}
        >
          {busyId === '*' ? 'Creating…' : 'Create invite'}
        </button>
      </div>

      {/* One-time link panel */}
      {oneTime && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid ' + C.am + '66', background: 'rgba(224,168,62,.07)', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.am }}>Invitation link</span>
            <span style={badgeS(C.am)}>shown once</span>
            <span style={badgeS(C.am)}>single use</span>
            <button onClick={() => setOneTime(null)} style={{ ...GBTN_SM, marginLeft: 'auto' }}>Dismiss</button>
          </div>
          <div style={{ fontSize: 11.5, color: C.am, lineHeight: 1.5 }}>
            Copy this link now — it is never shown again. Send it over a trusted channel to{' '}
            {oneTime.email ? <code style={{ fontFamily: MONO }}>{oneTime.email}</code> : 'the person you are inviting'} as a{' '}
            <strong>{oneTime.role}</strong>. It expires {fmtTS(parseTs(oneTime.expires_at))}.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              readOnly
              value={oneTime.url}
              aria-label="One-time invitation URL"
              onFocus={(e) => e.currentTarget.select()}
              style={{ ...INPUT, flex: 1, fontFamily: MONO, fontSize: 11.5, color: C.tx, userSelect: 'all' }}
            />
            <button onClick={copyUrl} style={{ ...GBTN_SM }}>{copied ? 'Copied ✓' : 'Copy link'}</button>
          </div>
        </div>
      )}

      {/* Pending invites table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              <th style={mTh('left')}>Role</th>
              <th style={mTh('left')}>Email</th>
              <th style={mTh('left')}>Created</th>
              <th style={mTh('left')}>Expires</th>
              <th style={mTh('right')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ ...mTd('left'), color: C.dm }}>Loading…</td></tr>
            )}
            {!loading && invites.length === 0 && (
              <tr><td colSpan={5} style={{ ...mTd('left'), color: C.dm }}>No pending invitations. Create one above to onboard a teammate.</td></tr>
            )}
            {invites.map((i) => {
              const expMs = parseTs(i.expires_at);
              const rowBusy = busyId === i.id;
              const isConfirming = confirmId === i.id;
              return (
                <tr key={i.id}>
                  <td style={mTd('left')}><span style={badgeS(C.bl)}>{i.role}</span></td>
                  <td style={{ ...mTd('left'), color: i.email ? C.tx : C.dm }}>{i.email ?? 'any email'}</td>
                  <td style={{ ...mTd('left'), color: C.dm }}>{fmtTS(parseTs(i.created_at))}</td>
                  <td style={{ ...mTd('left'), color: C.dm }} title={expMs ? fmtTS(expMs) : ''}>
                    {expMs ? 'in ' + fmtAge(expMs - serverNow) : '—'}
                  </td>
                  <td style={mTd('right')}>
                    {isConfirming ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 10.5, color: C.rd }}>Revoke?</span>
                        <button onClick={() => revoke(i.id)} disabled={rowBusy} style={{ ...GBTN_SM, color: C.rd, border: '1px solid ' + C.rd + '77', opacity: rowBusy ? 0.6 : 1 }}>
                          {rowBusy ? '…' : 'Yes'}
                        </button>
                        <button onClick={() => setConfirmId(null)} disabled={rowBusy} style={GBTN_SM}>No</button>
                      </span>
                    ) : (
                      <button onClick={() => { setConfirmId(i.id); setError(null); setNotice(null); }} disabled={!!busyId} style={{ ...GBTN_SM, color: C.rd, border: '1px solid ' + C.rd + '77', opacity: busyId ? 0.6 : 1 }}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
